/**
 * API client para o app `orcamentos/` do backend (Fase 4).
 *
 * Endpoints (sem trailing slash, padrao do projeto):
 *   GET    /api/orcamentos                          list (filtros: situacao/cliente/vendedor/prioridade)
 *   POST   /api/orcamentos                          criar (payload usa criar service)
 *   GET    /api/orcamentos/{id}
 *   PATCH  /api/orcamentos/{id}                     editar (meta apenas)
 *   DELETE /api/orcamentos/{id}                     (orcamento:delete + MFA)
 *   POST   /api/orcamentos/{id}/enviar
 *   POST   /api/orcamentos/{id}/aprovar
 *   POST   /api/orcamentos/{id}/recusar             body: {motivo}
 *   POST   /api/orcamentos/{id}/expirar
 *   POST   /api/orcamentos/{id}/nova-versao        body: {motivo_mudanca}
 *   POST   /api/orcamentos/{id}/converter-em-pedido body: {observacoes}
 *
 *   GET    /api/orcamento-versoes?orcamento={id}
 *
 *   CRUD: /api/ambientes (gate: versao do orcamento editavel)
 *   CRUD: /api/itens (gate: idem)
 */

const PREFIXO_ORC = "/api/orcamentos";
const PREFIXO_VERSAO = "/api/orcamento-versoes";
const PREFIXO_AMB = "/api/ambientes";
const PREFIXO_ITEM = "/api/itens";

// ---------- Orcamento ----------

export function listarOrcamentos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("search", params.busca);
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.cliente) usp.set("cliente", params.cliente);
  if (params.vendedor) usp.set("vendedor", params.vendedor);
  if (params.prioridade) usp.set("prioridade", params.prioridade);
  if (params.status_temporal) usp.set("status_temporal", params.status_temporal);
  if (params.ordering) usp.set("ordering", params.ordering);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_ORC}${qs ? `?${qs}` : ""}`);
}

export function obterOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}`);
}

// Cockpit do orcamento (Detalhe — Fase 2): cabecalho + KPIs (valor/margem/
// ambientes+m2/validade/probabilidade) + proxima acao + versoes + timeline.
// GET /api/orcamentos/{id}/cockpit. Tolerante a falha no caller (silentError).
export function obterCockpitOrcamento(request, id, options = {}) {
  return request(`${PREFIXO_ORC}/${id}/cockpit`, options);
}

// Diff entre a versao N e a anterior (N-1): ambientes/itens +/- e Δ valor.
// GET /api/orcamentos/{id}/diff-versao?versao=N.
export function diffVersaoOrcamento(request, id, numeroVersao, options = {}) {
  return request(`${PREFIXO_ORC}/${id}/diff-versao?versao=${numeroVersao}`, options);
}

export function criarOrcamento(request, dados) {
  return request(PREFIXO_ORC, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarOrcamento(request, id, dados) {
  return request(`${PREFIXO_ORC}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

// Bloco F.6: excluir orcamento (gate backend: orcamento:delete + MFA recente).
// Auditoria registra evento `orcamento_excluido`. Idempotente — se ja excluido
// retorna 404.
export function excluirOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}`, { method: "DELETE" });
}

// ---------- Bloco F.7: OrcamentoParceiro (M2M Orcamento <-> Representante) ----------

const PREFIXO_PARCEIRO = "/api/orcamento-parceiros";

export function listarParceirosDoOrcamento(request, orcamentoId) {
  return request(`${PREFIXO_PARCEIRO}?orcamento=${orcamentoId}`);
}

export function adicionarParceiroAoOrcamento(request, { orcamento, representante, comissao_percentual, observacao }) {
  return request(PREFIXO_PARCEIRO, {
    method: "POST",
    body: JSON.stringify({
      orcamento,
      representante,
      comissao_percentual,
      observacao: observacao || "",
    }),
  });
}

export function atualizarParceiroDoOrcamento(request, id, { comissao_percentual, observacao }) {
  const body = {};
  if (comissao_percentual !== undefined) body.comissao_percentual = comissao_percentual;
  if (observacao !== undefined) body.observacao = observacao;
  return request(`${PREFIXO_PARCEIRO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function removerParceiroDoOrcamento(request, id) {
  return request(`${PREFIXO_PARCEIRO}/${id}`, { method: "DELETE" });
}

/**
 * Bloco H — Padrao Transition Form: body opcional com `proxima_acao` e
 * `data_proxima_acao` permite atualizar o follow-up no momento do envio,
 * sem trip para tela de edicao. Backend valida apos atualizar.
 */
export function enviarOrcamento(request, id, {
  proxima_acao,
  data_proxima_acao,
  // ADR-0050 (motor DIVISOR) — confirmacoes explicitas dos gates G13/Q7:
  // so vao no body quando true (checkbox marcado no modal de envio).
  aceitar_estimativas_internas,
  confirmar_cotacoes_vencidas,
} = {}) {
  const body = {};
  if (proxima_acao !== undefined) body.proxima_acao = proxima_acao;
  if (data_proxima_acao !== undefined) body.data_proxima_acao = data_proxima_acao || null;
  if (aceitar_estimativas_internas) body.aceitar_estimativas_internas = true;
  if (confirmar_cotacoes_vencidas) body.confirmar_cotacoes_vencidas = true;
  const hasBody = Object.keys(body).length > 0;
  return request(`${PREFIXO_ORC}/${id}/enviar`, {
    method: "POST",
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
  });
}

export function aprovarOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/aprovar`, { method: "POST" });
}

export function recusarOrcamento(request, id, motivo) {
  return request(`${PREFIXO_ORC}/${id}/recusar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function expirarOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/expirar`, { method: "POST" });
}

// B7 (ADR-0037): catalogo de razoes estruturadas de perda (espelha
// comercial.models.RAZOES_PERDA do backend).
export const RAZOES_PERDA = [
  { codigo: "PRECO", label: "Preço acima do esperado" },
  { codigo: "PRAZO", label: "Prazo de entrega" },
  { codigo: "CONCORRENTE", label: "Fechou com concorrente" },
  { codigo: "ESCOPO", label: "Mudança de escopo / projeto" },
  { codigo: "DESISTENCIA", label: "Desistência do cliente" },
  { codigo: "SEM_RESPOSTA", label: "Cliente parou de responder" },
  { codigo: "OUTROS", label: "Outros" },
];

// B7: registra/atualiza a razao estruturada de perda. Desacoplado da
// transicao (pode ser chamado retroativamente em RECUSADO/EXPIRADO).
export function registrarRazaoPerda(request, id, { codigo, descricao = "" }) {
  return request(`${PREFIXO_ORC}/${id}/registrar-razao-perda`, {
    method: "POST",
    body: JSON.stringify({ codigo, descricao }),
  });
}

// Bloco E.2 v2 (ADR-0019): atualiza valor_unitario de items CAD com o
// custo_medio atual dos Produtos vinculados (refaz BOM explosion).
export function recalcularPrecosOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/recalcular-precos`, { method: "POST" });
}

// REF#18: cria um ambiente vazio na versao ativa do orcamento. O backend
// garante a versao 1 se faltar (ver `garantir_versao_ativa`), entao o frontend
// nao precisa mais conhecer o id da versao — elimina o erro
// "Versao ativa indisponivel" em orcamentos sem versao embedada.
export function criarAmbienteNoOrcamento(request, orcamentoId, { nome }) {
  return request(`${PREFIXO_ORC}/${orcamentoId}/criar-ambiente`, {
    method: "POST",
    body: JSON.stringify({ nome }),
  });
}

// Bloco E.2 v2 (ADR-0019): aplica snapshot atual da ConfiguracaoComercial
// (custo_op_padrao + margem_lucro_padrao) no orcamento. Usado quando gestor
// muda config padrao e vendedor decide aplicar no orcamento existente.
export function aplicarConfigPadrao(request, id) {
  return request(`${PREFIXO_ORC}/${id}/aplicar-config-padrao`, { method: "POST" });
}

// Bloco F.2: vendedor solicita aprovacao quando desconto excede
// desconto_max_sem_aprovacao_percentual da unidade. Backend cria evento
// auditado + envia email ao(s) usuario(s) com permissao
// 'comercial:aprovar_desconto'. Nao altera o desconto — gestor edita
// manualmente apos receber a solicitacao.
export function solicitarAprovacaoDesconto(request, id, { desconto_solicitado, justificativa }) {
  return request(`${PREFIXO_ORC}/${id}/solicitar-aprovacao-desconto`, {
    method: "POST",
    body: JSON.stringify({ desconto_solicitado, justificativa }),
  });
}

export function criarNovaVersao(request, id, motivoMudanca) {
  return request(`${PREFIXO_ORC}/${id}/nova-versao`, {
    method: "POST",
    body: JSON.stringify({ motivo_mudanca: motivoMudanca }),
  });
}

// ---------- Revisao (resposta ao pedido do lead via link publico) ----------
// iniciar: cria a nova versao (a anterior fica preservada) e abre pra editar.
// finalizar: fecha a revisao (a nova versao ja existe). cancelar: descarta a
// nova versao e volta a anterior. Todos retornam o orcamento atualizado.

export function iniciarRevisaoOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/iniciar-revisao`, { method: "POST" });
}

export function finalizarRevisaoOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/finalizar-revisao`, { method: "POST" });
}

export function cancelarRevisaoOrcamento(request, id) {
  return request(`${PREFIXO_ORC}/${id}/cancelar-revisao`, { method: "POST" });
}

export function converterEmPedido(request, id, observacoes = "") {
  return request(`${PREFIXO_ORC}/${id}/converter-em-pedido`, {
    method: "POST",
    body: JSON.stringify({ observacoes }),
  });
}

// ---------- OrcamentoVersao (read-only) ----------

export function listarVersoes(request, orcamentoId) {
  return request(`${PREFIXO_VERSAO}?orcamento=${orcamentoId}`);
}

// ---------- Ambiente ----------

export function listarAmbientes(request, versaoId) {
  return request(`${PREFIXO_AMB}?versao=${versaoId}`);
}

export function criarAmbiente(request, dados) {
  return request(PREFIXO_AMB, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarAmbiente(request, id, dados) {
  return request(`${PREFIXO_AMB}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarAmbiente(request, id) {
  return request(`${PREFIXO_AMB}/${id}`, { method: "DELETE" });
}

// ---------- Item ----------

export function listarItens(request, ambienteId) {
  return request(`${PREFIXO_ITEM}?ambiente=${ambienteId}`);
}

export function criarItem(request, dados) {
  return request(PREFIXO_ITEM, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarItem(request, id, dados) {
  return request(`${PREFIXO_ITEM}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarItem(request, id) {
  return request(`${PREFIXO_ITEM}/${id}`, { method: "DELETE" });
}
